import { useEffect, useState } from 'react';
import { Container, Paper, Button, Typography, Box, Stack, IconButton } from '@mui/material';
import { Grid } from '@mui/material';
import { updateBusinessStatus, updateStaffStatus, getStylistShowcaseCount, getLastListingDate, getWeeklyShowcaseCount, getCampaigns, updateUserFlag } from '@services/profileService';
import type { Campaign } from '@app-types/models/user';
import { getStylists } from '@services/stylistService';
import type { Stylist } from '@app-types/models/stylist';
import Mission from '@components/Mission';
import QuickListingWizard from '@components/QuickListingWizard';
import withUser from '@components/withUser';
import type { WithUserProps } from '@components/withUser';
import { useTranslation } from 'react-i18next';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText } from '@mui/material';
import QrCodeIcon from '@mui/icons-material/QrCode';
import DownloadIcon from '@mui/icons-material/Download';
import { QRCodeCanvas } from 'qrcode.react';
import { capitalize } from 'lodash';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import PlatformGrowth from '@components/PlatformGrowth';
import { generateReviewQr, generateWalkinQr } from '@services/liveReviewService';
import QuickReviewModal from '../components/reservation/QuickReviewModal';

type DashboardProps = WithUserProps;

const Dashboard = ({ user, fetchUserData }: DashboardProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const dashUser = user;
  const [showcaseCount, setShowcaseCount] = useState<number>(0);
  const [lastListingDate, setLastListingDate] = useState<string | null>(null);
  const [timeUntilNextWeek, setTimeUntilNextWeek] = useState<string>('');
  const [weeklyShowcaseCount, setWeeklyShowcaseCount] = useState<number>(0);
  const [numberOfWeeksHasPastBy, setNumberOfWeeksHasPastBy] = useState<number>(0);
  const [listingStylistsCount, setListingStylistsCount] = useState<number>(0);
  const [stylists, setStylists] = useState<Stylist[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [qrCodeOpen, setQrCodeOpen] = useState<boolean>(false);
  const [qrCodeValue, setQrCodeValue] = useState<string>('');
  const [qrCodeTitleKey, setQrCodeTitleKey] = useState<string>('dashboard.qrCodeBusiness');
  const [quickListingWizardOpen, setQuickListingWizardOpen] = useState<boolean>(false);
  const [activeWizardStep, setActiveWizardStep] = useState<number>(0);
  const [initialStylistIdForWizard, setInitialStylistIdForWizard] = useState<string | null>(null);
  const [firstTimeLoginDialogOpen, setFirstTimeLoginDialogOpen] = useState<boolean>(false);
  const [walkInQrOpen, setWalkInQrOpen] = useState<boolean>(false);
  const [walkInQrUrl, setWalkInQrUrl] = useState<string>('');
  const [walkInQrLoading, setWalkInQrLoading] = useState<boolean>(false);
  const [walkinModalOpen, setWalkinModalOpen] = useState<boolean>(false);

  useEffect(() => {
    if (dashUser) {
      if (dashUser.flags && dashUser.flags.firstTimeLogin) {
        console.log('Dashboard: First time login detected.');
        setFirstTimeLoginDialogOpen(true);
        updateUserFlag('firstTimeLogin', false)
          .then(() => {
            console.log('Successfully updated firstTimeLogin flag to false');
          })
          .catch((error: unknown) => {
            console.error('Failed to update firstTimeLogin flag:', error);
          });
      }

      if (dashUser.role === 'stylist') {
        getStylistShowcaseCount(dashUser._id).then((count) => setShowcaseCount(count)).catch((error: unknown) => console.error('Error fetching showcase count:', error));
        getLastListingDate().then((response) => {
          const date = response.lastListingDate;
          setLastListingDate(date);
          if (date) calculateTimeUntilNextWeek(date);
        }).catch((error: unknown) => console.error('Error fetching last listing date:', error));
        getWeeklyShowcaseCount().then((response) => {
          setWeeklyShowcaseCount(response.currentWeekCount ?? 0);
          setNumberOfWeeksHasPastBy(response.weeksPassed ?? 0);
        }).catch((error: unknown) => console.error('Error fetching weekly showcase count:', error));
      } else if (dashUser.businessName) {
        getStylists().then((result) => {
          const stylistsData = Array.isArray(result) ? result : [];
          const listingStylistsCount = stylistsData.filter(stylist => stylist.status === 'listing').length;
          setListingStylistsCount(listingStylistsCount);
          // Also stash the full list so the Quick Live Review modal can offer a stylist picker.
          setStylists(stylistsData);
        }).catch((error: unknown) => console.error('Error fetching stylists:', error));
      }
      getCampaigns().then((result) => setCampaigns(Array.isArray(result) ? result : [])).catch((error: unknown) => console.error('Error fetching campaigns:', error));
    }
  }, [dashUser]);

  // Helper function to clean up wizard state
  const cleanupWizardState = () => {
    // Clean up URL parameters
    const url = new URL(window.location.href);
    if (url.searchParams.has('wizard') || url.searchParams.has('step') || url.searchParams.has('instagram_success')) {
      url.searchParams.delete('wizard');
      url.searchParams.delete('step');
      url.searchParams.delete('instagram_success');
      window.history.replaceState({}, '', url.toString());
    }

    // Clean up session storage
    sessionStorage.removeItem('wizard_open');
    sessionStorage.removeItem('wizard_step');
    sessionStorage.removeItem('wizard_stylist_id');
    console.log('Dashboard: Cleaned URL and sessionStorage.');
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const wizardParam = searchParams.get('wizard');
    const stepParam = searchParams.get('step');
    const instagramSuccess = searchParams.get('instagram_success');

    if (wizardParam === 'true' && stepParam) {
      // If instagram_success=true, user just completed Instagram onboarding
      // Skip wizard since they already set up profile through that flow
      if (instagramSuccess === 'true') {
        console.log('Dashboard: Skipping wizard - user completed Instagram onboarding');
        cleanupWizardState();
        return;  // Exit early, don't open wizard
      }

      console.log('Dashboard: Detected redirect with wizard params. Attempting to open wizard.');

      if (!dashUser?.flags?.quickSetupWizardDone && !(location.state as Record<string, unknown> | null)?.newUser) {
        const stepNumber = parseInt(stepParam, 10);
        const restoredStylistId = sessionStorage.getItem('wizard_stylist_id');

        setActiveWizardStep(stepNumber);
        if (restoredStylistId) {
          setInitialStylistIdForWizard(restoredStylistId);
          console.log('Dashboard: Restored stylistId from session:', restoredStylistId);
        }
        setQuickListingWizardOpen(true);
      } else {
        console.log('Dashboard: Quick setup wizard already completed, not reopening after redirect.');
      }

      // Clean up wizard state
      cleanupWizardState();
    }
  }, [location, dashUser?.flags?.quickSetupWizardDone]);

  const handleWizardClose = () => {
    console.log('Dashboard: Closing QuickListingWizard.');
    setQuickListingWizardOpen(false);
    setActiveWizardStep(0);
    setInitialStylistIdForWizard(null);

    // Clean up wizard state
    cleanupWizardState();
  };

  const handleStartListing = async () => {
    const response = await updateBusinessStatus({status: 'listing'});
    console.log('response', response);
    fetchUserData();
  }

  const handleStopListing = async () => {
    const response = await updateBusinessStatus({status: 'approved'});
    console.log('response', response);
    fetchUserData();
  }

  const handleStartListingStylist = async () => {
    const response = await updateStaffStatus({staffId: dashUser._id, status: 'listing'});
    console.log('response', response);
    fetchUserData();
  }

  const handleStopListingStylist = async () => {
    const response = await updateStaffStatus({staffId: dashUser._id, status: 'suspended'});
    console.log('response', response);
    fetchUserData();
  }

  const calculateTimeUntilNextWeek = (lastDate: string) => {
    const lastListing = new Date(lastDate);
    const nextWeek = new Date(lastListing);
    nextWeek.setDate(lastListing.getDate() + 7);

    const updateCountdown = () => {
      const now = new Date();
      const timeDifference = nextWeek.getTime() - now.getTime();
      if (timeDifference > 0) {
        const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((timeDifference / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((timeDifference / (1000 * 60)) % 60);
        const seconds = Math.floor((timeDifference / 1000) % 60);
        setTimeUntilNextWeek(`${days}d ${hours}h ${minutes}m ${seconds}s`);
      } else {
        setTimeUntilNextWeek('Time to upload new showcases!');
      }
    };

    updateCountdown();
    const intervalId = setInterval(updateCountdown, 1000);

    return () => clearInterval(intervalId);
  };

  const handleOpenQrCodeDialog = (value: string, type: string) => {
    setQrCodeValue(value);
    setQrCodeTitleKey(type === 'business' ? 'dashboard.qrCodeBusiness' : 'dashboard.qrCodeCustomer');
    setQrCodeOpen(true);
  };

  const handleCloseQrCodeDialog = () => {
    setQrCodeOpen(false);
  };

  const handleFirstTimeLoginDialogClose = () => {
    setFirstTimeLoginDialogOpen(false);
  };

  const handleStartQuickListingWizard = () => {
    setFirstTimeLoginDialogOpen(false);
    setQuickListingWizardOpen(true);
  };

  const handleOpenWalkInQr = async () => {
    setWalkInQrOpen(true);
    setWalkInQrLoading(true);
    try {
      // B6: a walk-in QR must call generateWalkinQr() (single-use jti, verifiedVisit=true),
      // NOT generateReviewQr() (reusable poster, verifiedVisit=false, no single-use).
      const { reviewUrl } = await generateWalkinQr();
      setWalkInQrUrl(reviewUrl);
    } catch (error: unknown) {
      console.error('Error generating walk-in review QR:', error);
      toast.error(t('liveReview.qrError'));
      setWalkInQrOpen(false);
    } finally {
      setWalkInQrLoading(false);
    }
  };

  const handleCloseWalkInQr = () => {
    setWalkInQrOpen(false);
    setWalkInQrUrl('');
  };

  const handleDownloadWalkInQr = () => {
    const canvas = document.getElementById('walk-in-review-qr') as HTMLCanvasElement | null;
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = 'walk-in-review-qr.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  return (
    dashUser && (
      <Box sx={{ py: 3 }}>
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h4" sx={{ mb: 3 }}>
                {t('dashboard.welcome', { name: (dashUser.displayName || dashUser.businessName || t('dashboard.stylist')) })}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <PlatformGrowth />
            </Grid>

            {(!dashUser.flags || !dashUser.flags.quickSetupWizardDone) && !(location.state as Record<string, unknown> | null)?.newUser && (
              <Grid size={{ xs: 12, md: 12 }}>
                <Paper sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h5" gutterBottom>
                    {t('dashboard.quickListingWizard')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('dashboard.quickListingWizardDescription', 'Help you quickly listing your stylist on hkstylist platform')}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setQuickListingWizardOpen(true)}
                    sx={{ mb: 3 }}
                  >
                    {t('dashboard.quickListingWizard')}
                  </Button>
                </Paper>
              </Grid>
            )}

            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h5" gutterBottom>
                  {t('dashboard.userInfo')}
                </Typography>

                <Stack spacing={1} sx={{ mb: 3 }}>
                  <Typography>
                    <strong>{t('dashboard.name')}:</strong> {dashUser.displayName || dashUser.businessName}
                  </Typography>
                  <Typography>
                    <strong>{t('dashboard.email')}:</strong> {dashUser.email}
                  </Typography>
                  <Typography>
                    <strong>{t('dashboard.role')}:</strong> {capitalize(dashUser.role)}
                  </Typography>

                  {dashUser.credit && (
                    <Typography>
                      <strong>{t('dashboard.credit')}:</strong> {dashUser.credit}
                    </Typography>
                  )}

                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography>
                      <strong>{t('dashboard.referralCode')}:</strong> {dashUser.referralCode}
                    </Typography>
                    <IconButton onClick={() => navigator.clipboard.writeText(dashUser.referralCode || '')} size="small">
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography>
                      <strong>{t('dashboard.referralLinkBusiness')}:</strong>
                      <br />
                      <a
                        href={`${import.meta.env.VITE_BASE_URL}/onboarding?referral_code=${dashUser.referralCode}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                      >
                        {`${import.meta.env.VITE_BASE_URL}/onboarding?referral_code=${dashUser.referralCode}`}
                      </a>
                    </Typography>
                    <IconButton onClick={() => navigator.clipboard.writeText(`${import.meta.env.VITE_BASE_URL}/onboarding?referral_code=${dashUser.referralCode}`)} size="small">
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                    <IconButton onClick={() => handleOpenQrCodeDialog(`${import.meta.env.VITE_BASE_URL}/onboarding?referral_code=${dashUser.referralCode}`, 'business')} size="small">
                      <QrCodeIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography>
                      <strong>{t('dashboard.referralLinkCustomer')}:</strong>
                      <br />
                      <a
                          href={`${import.meta.env.VITE_SITE_URL}/#/account/signup?referral_code=${dashUser.referralCode}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                      >
                        {`${import.meta.env.VITE_SITE_URL}/#/account/signup?referral_code=${dashUser.referralCode}`}
                      </a>
                    </Typography>
                    <IconButton onClick={() => navigator.clipboard.writeText(`${import.meta.env.VITE_SITE_URL}/#/account/signup?referral_code=${dashUser.referralCode}`)} size="small">
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                    <IconButton onClick={() => handleOpenQrCodeDialog(`${import.meta.env.VITE_SITE_URL}/#/account/signup?referral_code=${dashUser.referralCode}`, 'customer')} size="small">
                      <QrCodeIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>

                {(dashUser.role === 'stylist' || dashUser.businessName) && (
                  <Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Button variant="contained" startIcon={<QrCodeIcon />} onClick={() => setWalkinModalOpen(true)}>
                        {t('liveReview.walkinQr')}
                      </Button>
                      <Button variant="outlined" startIcon={<QrCodeIcon />} onClick={handleOpenWalkInQr}>
                        {t('liveReview.myReviewQr')}
                      </Button>
                    </Stack>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {t('liveReview.walkinQrHint')}
                    </Typography>
                  </Box>
                )}

                {dashUser.role === 'stylist' && (
                  <Stack spacing={2}>
                    <Box>
                      {t('dashboard.stylistStatus')}: {capitalize(dashUser.status || '')}
                    </Box>

                    {dashUser.status === 'listing' ? (
                      <Stack spacing={2}>
                        <Box>{t('dashboard.listingCongrats')}</Box>
                        <Box>
                          {t('dashboard.lastListingDate')} <strong>{new Date(lastListingDate!).toLocaleString()}</strong>
                        </Box>
                        <Button variant="contained" onClick={handleStopListingStylist}>{t('dashboard.stopListing')}</Button>
                      </Stack>
                    ) : (
                      <Stack spacing={2}>
                        <Box>{t('dashboard.nextStep')}</Box>
                        <Box sx={{ wordBreak: 'break-all' }}>
                          {t('dashboard.notListing')}
                        </Box>
                        <Box>{t('dashboard.validShowcases', { count: showcaseCount })}</Box>

                        {showcaseCount < 2 ? (
                          <Box>{t('dashboard.uploadShowcases')}</Box>
                        ) : (
                          <>
                            <Box>{t('dashboard.enoughShowcases')}</Box>
                            <Button variant="contained" onClick={handleStartListingStylist}>
                              {t('dashboard.startListing')}
                            </Button>
                          </>
                        )}
                      </Stack>
                    )}
                  </Stack>
                )}

                {dashUser.businessName && (
                  <Stack spacing={2}>
                    <Box>
                      <strong>{t('dashboard.businessStatus')}:</strong> {capitalize(dashUser.status || '')}
                    </Box>

                    {dashUser.status === 'listing' ? (
                      <Stack spacing={2}>
                        <Box>{t('dashboard.businessCongrats')}</Box>
                        <Button variant="contained" onClick={handleStopListing}>{t('dashboard.stopListing')}</Button>
                      </Stack>
                    ) : (
                      <Stack spacing={2}>
                        <Box>{t('dashboard.nextStep')}</Box>
                        <Box>{t('dashboard.businessNotListing', { count: listingStylistsCount })}</Box>
                        {listingStylistsCount > 0 && (
                          <Button variant="contained" onClick={handleStartListing}>
                            {t('dashboard.startListing')}
                          </Button>
                        )}
                        <Button
                          variant="contained"
                          href={`${import.meta.env.VITE_BASE_URL}/stylist`}
                        >
                          {t('common.goToStylistPage')}
                        </Button>
                      </Stack>
                    )}
                  </Stack>
                )}
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Mission
                  numberOfWeeksHasPastBy={numberOfWeeksHasPastBy}
                  weeklyShowcaseCount={weeklyShowcaseCount}
                  timeUntilNextWeek={timeUntilNextWeek}
                  campaigns={campaigns}
                  user={user}
                />
              </Paper>
            </Grid>
          </Grid>
        </Container>

        <Dialog open={qrCodeOpen} onClose={handleCloseQrCodeDialog}>
          <DialogTitle>{t(qrCodeTitleKey)}</DialogTitle>
          <DialogContent>
            <QRCodeCanvas value={qrCodeValue} size={256} />
          </DialogContent>
        </Dialog>

        <QuickReviewModal open={walkinModalOpen} onClose={() => setWalkinModalOpen(false)} reservation={null} walkin stylists={stylists} />

        <Dialog open={walkInQrOpen} onClose={handleCloseWalkInQr} maxWidth="xs" fullWidth>
          <DialogTitle>{t('liveReview.myReviewQr')}</DialogTitle>
          <DialogContent sx={{ textAlign: 'center' }}>
            {walkInQrLoading || !walkInQrUrl ? (
              <Typography sx={{ py: 4 }}>{t('common.loading')}</Typography>
            ) : (
              <>
                <Box sx={{ display: 'inline-block', p: 2, bgcolor: '#fff' }}>
                  <QRCodeCanvas id="walk-in-review-qr" value={walkInQrUrl} size={256} />
                </Box>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {t('liveReview.walkInInstruction')}
                </Typography>
                <Typography
                  variant="body2"
                  component='a'
                  href={walkInQrUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  sx={{
                    mt: 1.5,
                    display: 'inline-block',
                    color: 'primary.main',
                    fontWeight: 600,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {t('liveReview.qr.openInNewTab')}
                </Typography>
              </>
            )}
          </DialogContent>
          <DialogActions>
            {walkInQrUrl && (
              <Button startIcon={<DownloadIcon />} onClick={handleDownloadWalkInQr}>
                {t('liveReview.download')}
              </Button>
            )}
            <Button onClick={handleCloseWalkInQr}>{t('common.close')}</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={firstTimeLoginDialogOpen}
          onClose={handleFirstTimeLoginDialogClose}
          aria-labelledby="first-time-login-dialog-title"
          aria-describedby="first-time-login-dialog-description"
        >
          <DialogTitle id="first-time-login-dialog-title">
            {t('dashboard.welcomeFirstTime')}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="first-time-login-dialog-description">
              {t('dashboard.firstTimeLoginMessage')}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleFirstTimeLoginDialogClose} color="primary">
              {t('common.later')}
            </Button>
            <Button onClick={handleStartQuickListingWizard} color="primary" variant="contained" autoFocus>
              {t('dashboard.getStarted')}
            </Button>
          </DialogActions>
        </Dialog>

        <QuickListingWizard
          open={quickListingWizardOpen}
          onClose={handleWizardClose}
          fetchUserData={fetchUserData}
          initialStep={activeWizardStep}
          initialStylistId={initialStylistIdForWizard}
        />
      </Box>
    )
  );
};

const WrappedDashboard = withUser(Dashboard);
export default WrappedDashboard;
